import Phaser from 'phaser';
import { TEXTURE_KEYS } from '../assets/assetKeys';
import { PALETTE } from '../assets/palette';
import { enhanceText } from '../utils/TextQuality';

export const UI_FONT =
  '"Segoe UI Variable", "Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, "Inter", Roboto, "Helvetica Neue", Arial, sans-serif';

/** Warna teks dengan kontras tinggi di atas background langit/hijau terang */
export const UI_COLORS = {
  text: '#ffffff',
  textSoft: '#ffffff',
  accent: '#ffd966',
  accentAlt: '#ffb86c',
  muted: '#eef2f8',
  success: '#9cde8e',
  danger: '#ff8787',
  stroke: '#0a1218',
  strokeSoft: '#1a2a1c',
} as const;

export class UIStyle {
  /** Ukuran font responsif — sedikit lebih besar & tajam di layar HD */
  static px(base: number, mobile = false): string {
    return `${base + (mobile ? 2 : 1)}px`;
  }

  static createRoundedPanel(
    scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    height: number,
    textureKey: string = TEXTURE_KEYS.UI_DIALOGUE,
    originX = 0,
    originY = 0,
  ): Phaser.GameObjects.Image {
    const panel = scene.add.image(x, y, textureKey);
    panel.setDisplaySize(width, height);
    panel.setOrigin(originX, originY);
    return panel;
  }

  static createDarkOverlay(
    scene: Phaser.Scene,
    width: number,
    height: number,
    alpha = 0.45,
  ): Phaser.GameObjects.Rectangle {
    return scene.add.rectangle(width / 2, height / 2, width, height, 0x000000, alpha);
  }

  static createContentCard(
    scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    height: number,
  ): Phaser.GameObjects.Graphics {
    const g = scene.add.graphics();
    g.fillStyle(0x10202d, 0.84);
    g.fillRoundedRect(x, y, width, height, 20);
    g.fillStyle(0x29425d, 0.28);
    g.fillRoundedRect(x + 6, y + 6, width - 12, Math.max(0, height * 0.2), 14);
    g.fillStyle(0xffffff, 0.03);
    g.fillRoundedRect(x + 8, y + 8, width - 16, height - 16, 14);
    g.lineStyle(2, 0xe7c487, 0.42);
    g.strokeRoundedRect(x, y, width, height, 20);
    return g;
  }

  static createModalSurface(
    scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    height: number,
    options?: { showHeader?: boolean },
  ): Phaser.GameObjects.Graphics {
    const left = x - width / 2;
    const top = y - height / 2;
    const showHeader = options?.showHeader ?? true;
    const g = scene.add.graphics();
    g.fillStyle(0x0b1620, 1);
    g.fillRoundedRect(left, top, width, height, 24);
    if (showHeader) {
      g.fillStyle(0x1f3347, 1);
      g.fillRoundedRect(left + 8, top + 8, width - 16, Math.max(42, height * 0.16), 18);
    }
    g.lineStyle(2, 0xe7c487, 0.75);
    g.strokeRoundedRect(left, top, width, height, 24);
    return g;
  }

  static createDialoguePanel(
    scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    height: number,
  ): Phaser.GameObjects.Graphics {
    const left = x - width / 2;
    const top = y - height / 2;
    const g = scene.add.graphics();
    g.fillStyle(0x0b1620, 1);
    g.fillRoundedRect(left, top, width, height, 18);
    g.fillStyle(0x132433, 1);
    g.fillRoundedRect(left + 1, top + 1, width - 2, height - 2, 17);
    g.lineStyle(2, 0xd4a373, 0.9);
    g.strokeRoundedRect(left, top, width, height, 18);
    return g;
  }

  static createSpeakerTab(
    scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    height: number,
  ): Phaser.GameObjects.Graphics {
    const g = scene.add.graphics();
    g.fillStyle(0x162636, 1);
    g.fillRoundedRect(x, y, width, height, 10);
    g.lineStyle(2, 0xd4a373, 0.9);
    g.strokeRoundedRect(x, y, width, height, 10);
    return g;
  }

  static textStyle(size: string, color: string = UI_COLORS.text, bold = false): Phaser.Types.GameObjects.Text.TextStyle {
    return {
      fontSize: size,
      color,
      fontFamily: UI_FONT,
      fontStyle: bold ? 'bold' : 'normal',
      padding: { x: 2, y: 2 },
    };
  }

  /** Label di peta dunia — stroke tebal, kontras tinggi */
  static mapLabel(sizePx: number, color: string, bold = true): Phaser.Types.GameObjects.Text.TextStyle {
    return UIStyle.onBrightBg(UIStyle.px(sizePx), color, bold);
  }

  /** Label peta dengan word-wrap — mencegah teks panjang terpotong di tepi layar */
  static mapLabelWrapped(
    sizePx: number,
    color: string,
    wrapWidth: number,
    bold = true,
  ): Phaser.Types.GameObjects.Text.TextStyle {
    return {
      ...UIStyle.mapLabel(sizePx, color, bold),
      align: 'center',
      wordWrap: { width: wrapWidth, useAdvancedWrap: true },
      lineSpacing: 4,
    };
  }

  static addMapLabel(
    scene: Phaser.Scene,
    x: number,
    y: number,
    text: string,
    sizePx: number,
    color: string,
    wrapWidth = 128,
  ): Phaser.GameObjects.Text {
    return scene.add.text(x, y, text, UIStyle.mapLabelWrapped(sizePx, color, wrapWidth)).setOrigin(0.5, 1);
  }

  /** Nama pemain/NPC di atas kepala — teks saja, tanpa background */
  static nameTag(sizePx = 13): Phaser.Types.GameObjects.Text.TextStyle {
    return UIStyle.onBrightBg(UIStyle.px(sizePx), '#ffffff', true);
  }

  /** Teks di panel gelap — tanpa blur agar tetap tajam */
  static onDarkPanel(
    size: string,
    color: string = UI_COLORS.textSoft,
    bold = false,
  ): Phaser.Types.GameObjects.Text.TextStyle {
    return {
      ...UIStyle.textStyle(size, color, bold),
      stroke: '#000000',
      strokeThickness: bold ? 4 : 3,
    };
  }

  static panelTitle(size: string): Phaser.Types.GameObjects.Text.TextStyle {
    return UIStyle.onDarkPanel(size, UI_COLORS.accent, true);
  }

  static panelBody(size: string): Phaser.Types.GameObjects.Text.TextStyle {
    return {
      ...UIStyle.onDarkPanel(size, UI_COLORS.text, false),
      lineSpacing: 6,
    };
  }

  static panelMuted(size: string): Phaser.Types.GameObjects.Text.TextStyle {
    return UIStyle.onDarkPanel(size, UI_COLORS.muted, false);
  }

  static buttonLabel(size: string): Phaser.Types.GameObjects.Text.TextStyle {
    return UIStyle.onDarkPanel(size, '#ffffff', true);
  }

  /** Teks di atas background terang (menu, zona) */
  static onBrightBg(
    size: string,
    color: string = UI_COLORS.text,
    bold = false,
  ): Phaser.Types.GameObjects.Text.TextStyle {
    return {
      ...UIStyle.textStyle(size, color, bold),
      stroke: UI_COLORS.stroke,
      strokeThickness: bold ? 6 : 5,
    };
  }

  static subtitleOnBrightBg(size: string): Phaser.Types.GameObjects.Text.TextStyle {
    return UIStyle.onBrightBg(size, UI_COLORS.accent, true);
  }

  static titleShadow(scene: Phaser.Scene, x: number, y: number, text: string, size: string): Phaser.GameObjects.Text {
    const shadow = scene.add.text(x + 2, y + 2, text, {
      ...UIStyle.textStyle(size, '#000000', true),
      stroke: UI_COLORS.stroke,
      strokeThickness: 8,
    });
    shadow.setOrigin(0.5);
    shadow.setAlpha(0.5);
    return enhanceText(shadow);
  }

  static accentColor(): string {
    return UI_COLORS.accent;
  }

  static panelStroke(g: Phaser.GameObjects.Graphics, w: number, h: number): void {
    g.lineStyle(2, PALETTE.woodLight, 0.7);
    g.strokeRoundedRect(0, 0, w, h, 12);
  }
}
